// /////////////////////////////////////////////////////////////
// [ファイル] AssistantAgentDialog.tsx
// [概要] AI アシスタントの「エージェント設定」ダイアログ。3 ペイン構成で、
//        左レール (区分 / グループ / 自動化 / AI へ渡す文字数) → 中央 (一覧) → 右 (詳細・編集) の流れで管理する。
//        - 定義 (ルール / スキル / ワークフロー / 取組み課題) の閲覧・新規作成・編集・削除・有効切替
//        - 所属グループの付け替え (編集フォームの選択、または一覧の行を左レールのグループへドラッグ)
//        - グループの作成・編集・削除 (旧 AssistantGroupDialog を統合)
//        - メール自動処理 (AssistantMailAutomationTab) を「自動化」の面として表示
//        保存は楽観ロック付き (他の利用者や AI が先に更新していれば 409 → 差分を提示)。
//        保存・削除は Node 関数 manageAgent / manageAgentGroup 経由 (useAssistantAgents)。AI ツール経由の更新と併存する。
//        設定画面は「定義とグループの管理」と「注入量の管理」に絞り、開いているチャットの文脈には依存しない。
// /////////////////////////////////////////////////////////////
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Chip, Dialog, DialogTitle, IconButton, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { AnimatePresence, motion } from 'framer-motion';
import * as Icons from '../constants/icons';
import AssistantMailAutomationTab from './AssistantMailAutomationTab';
import { useStaffNames } from '../hooks/useStaffNames';
import { useNotification } from '../providers/NotificationProvider';
import { useConfirm } from '../providers/ConfirmProvider';
import {
  AgentConflictError,
  type AgentBuckets,
  type AgentGroup,
  type AgentItem,
  type AgentType,
  type IssueStatus,
  type SaveAgentInput,
  type SaveGroupInput,
} from '../hooks/useAssistantAgents';
import AgentRail, { type MeterRow } from './assistantAgent/AgentRail';
import AgentList from './assistantAgent/AgentList';
import AgentDetail, { type AgentEditForm } from './assistantAgent/AgentDetail';
import AgentGroupDetail, { type GroupEditForm } from './assistantAgent/AgentGroupDetail';
import {
  EASE_OUT,
  KIND_HINT,
  KIND_LABEL,
  SaveBar,
  audienceOf,
  charsOf,
  isInjected,
  sameSet,
  useStaffOptions,
  type ListFilter,
  type Section,
} from './assistantAgent/agentSettingsShared';

interface Props {
  open: boolean;
  onClose: () => void;
  loading: boolean; // 開くたびの再取得中はローディング表示する
  agents: AgentBuckets;
  groups: AgentGroup[];
  basePromptChars: number | null;
  currentUserEmail: string;
  onSaveAgent: (input: SaveAgentInput) => Promise<void>;
  onAssignGroup: (id: string, groupId: string, expectedUpdatedAt?: number) => Promise<void>;
  onDeleteAgent: (id: string) => Promise<void>;
  onSaveGroup: (input: SaveGroupInput) => Promise<void>;
  onDeleteGroup: (id: string) => Promise<void>;
  onRefresh?: () => Promise<void>; // 競合時に一覧を最新化する
  onOpenSession?: (sessionId: string) => void; // 「自動処理」の処理履歴から会話を開く
}

// 作成中のグループを指す仮のキー (保存後に実 id へ置き換える)
const NEW_GROUP_KEY = '__new_group__';
// 狭い幅 (スマホ等) では 1 ペインずつ横にスライドして見せる
const NARROW_QUERY = '(max-width:760px)';

type MobilePane = 'rail' | 'list' | 'detail';

export default function AssistantAgentDialog({
  open,
  onClose,
  loading,
  agents,
  groups,
  basePromptChars,
  currentUserEmail,
  onSaveAgent,
  onAssignGroup,
  onDeleteAgent,
  onSaveGroup,
  onDeleteGroup,
  onRefresh,
  onOpenSession,
}: Props) {
  const notify = useNotification();
  const confirm = useConfirm();
  const { formatMembers, resolve } = useStaffNames();
  const staffOptions = useStaffOptions(currentUserEmail);
  const narrow = useMediaQuery(NARROW_QUERY);

  // --- 派生データ ---------------------------------------------------------------
  const allItems = useMemo(
    () => [...agents.rules, ...agents.skills, ...agents.workflows, ...agents.issues],
    [agents]
  );
  const itemById = useMemo(() => new Map(allItems.map((a) => [a.id, a])), [allItems]);
  const groupById = useMemo(() => new Map(groups.map((g) => [g.id, g])), [groups]);
  const enabledGroups = useMemo(() => groups.filter((g) => g.enabled), [groups]);
  const countByType = useMemo(() => {
    const c: Record<AgentType, number> = { rule: 0, skill: 0, workflow: 0, issue: 0 };
    for (const a of allItems) c[a.type] += 1;
    return c;
  }, [allItems]);
  const countByGroup = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of allItems) m.set(a.group_id ?? '', (m.get(a.group_id ?? '') ?? 0) + 1);
    return m;
  }, [allItems]);
  // 注入文字数 (バックエンドと同じ数え方)。'' は共通
  const injectedCharsByGroup = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of allItems) {
      if (!isInjected(a)) continue;
      const key = a.group_id ?? '';
      m.set(key, (m.get(key) ?? 0) + charsOf(a));
    }
    return m;
  }, [allItems]);
  const commonChars = injectedCharsByGroup.get('') ?? 0;
  const meterRows = useMemo<MeterRow[]>(
    () => [
      { key: '', label: 'グループなしのチャット', common: commonChars, own: 0 },
      ...groups.map((g) => ({ key: g.id, label: g.name || '(名称未設定)', common: commonChars, own: injectedCharsByGroup.get(g.id) ?? 0 })),
    ],
    [groups, commonChars, injectedCharsByGroup]
  );

  // --- 画面状態 ---------------------------------------------------------------
  const [section, setSection] = useState<Section>('rule');
  const [groupKey, setGroupKey] = useState(''); // section='groups' の対象
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<ListFilter>('all');
  const [mobilePane, setMobilePane] = useState<MobilePane>('list');
  // 定義の編集フォーム (null = 閲覧)。id='' は新規
  const [form, setForm] = useState<AgentEditForm | null>(null);
  const [conflict, setConflict] = useState<AgentItem | null>(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  // グループの編集フォーム (null = 閲覧)
  const [groupForm, setGroupForm] = useState<GroupEditForm | null>(null);
  const [groupSaving, setGroupSaving] = useState(false);
  const [groupDeleting, setGroupDeleting] = useState(false);
  // 作成したグループを保存後に選び直すための名前 (manageAgentGroup は id を返さないため名前で探す)
  const [pendingGroupName, setPendingGroupName] = useState<string | null>(null);
  // ドラッグ中の定義 (DragOverlay とレールのドロップ可能表示に使う)
  const [draggingItem, setDraggingItem] = useState<AgentItem | null>(null);

  const selectedItem = selectedId ? itemById.get(selectedId) ?? null : null;
  const isNewAgent = form !== null && form.id === '';
  const isNewGroup = groupForm !== null && groupForm.id === undefined;

  // 編集権限 (バックエンドと同じ判定)。グループ付きは参照できる時点で編集可、共通は作成者または共有メンバー
  const canEdit = useCallback(
    (a: AgentItem): boolean => {
      if (a.group_id) return groupById.has(a.group_id);
      if (a.scope === 'personal') return a.user_id === currentUserEmail;
      return a.user_id === currentUserEmail || (a.shared_with ?? []).includes(currentUserEmail);
    },
    [groupById, currentUserEmail]
  );

  const audienceOthers = useCallback(
    (a: AgentItem) => audienceOf(a, a.group_id ?? '', groupById).filter((e) => e !== currentUserEmail),
    [groupById, currentUserEmail]
  );

  // 未保存の変更があるか (定義フォーム / グループフォームのどちらか)
  const agentDirty = useMemo(() => {
    if (!form) return false;
    if (form.id === '') return true;
    const a = itemById.get(form.id);
    if (!a) return true;
    return (
      form.name !== a.name ||
      form.description !== (a.description ?? '') ||
      form.body !== (a.body ?? '') ||
      form.enabled !== (a.enabled !== false) ||
      form.status !== (a.status ?? 'open') ||
      form.groupId !== (a.group_id ?? '') ||
      form.scope !== a.scope ||
      !sameSet(
        form.members.map((m) => m.email),
        (a.shared_with ?? []).filter((e) => e !== a.user_id && e !== currentUserEmail)
      )
    );
  }, [form, itemById, currentUserEmail]);
  const groupDirty = useMemo(() => {
    if (!groupForm) return false;
    if (!groupForm.id) return true;
    const g = groupById.get(groupForm.id);
    if (!g) return true;
    return (
      groupForm.name !== g.name ||
      groupForm.description !== (g.description ?? '') ||
      groupForm.scope !== g.scope ||
      groupForm.enabled !== g.enabled ||
      !sameSet(
        groupForm.members.map((m) => m.email),
        g.shared_with.filter((e) => e !== g.user_id && e !== currentUserEmail)
      )
    );
  }, [groupForm, groupById, currentUserEmail]);
  const dirty = agentDirty || groupDirty;

  // ダイアログを閉じたら状態を戻す
  useEffect(() => {
    if (!open) {
      setForm(null);
      setConflict(null);
      setGroupForm(null);
      setPendingGroupName(null);
      setDraggingItem(null);
      setQuery('');
      setFilter('all');
      setMobilePane('list');
    }
  }, [open]);

  // 作成したグループが一覧に現れたら、その面へ移る
  useEffect(() => {
    if (!pendingGroupName) return;
    const g = groups.find((x) => x.name === pendingGroupName && x.user_id === currentUserEmail);
    if (g) {
      setPendingGroupName(null);
      setGroupKey(g.id);
    }
  }, [groups, pendingGroupName, currentUserEmail]);

  // 選んでいた定義が消えた (削除・共有解除) ら選択を外す
  useEffect(() => {
    if (selectedId && !itemById.has(selectedId) && !(form && form.id === '')) {
      setSelectedId(null);
    }
  }, [itemById, selectedId, form]);

  // --- 離脱ガード ---------------------------------------------------------------
  const confirmDiscard = useCallback(async (): Promise<boolean> => {
    if (!dirty) return true;
    return confirm({
      title: '未保存の変更があります',
      message: '編集中の内容を破棄して移動しますか？',
      confirmText: '破棄して移動',
      destructive: true,
    });
  }, [dirty, confirm]);

  const resetEditing = useCallback(() => {
    setForm(null);
    setConflict(null);
    setGroupForm(null);
    if (groupKey === NEW_GROUP_KEY) setGroupKey('');
  }, [groupKey]);

  const navTo = async (next: Section, key?: string) => {
    if (!(await confirmDiscard())) return;
    resetEditing();
    setSection(next);
    if (next === 'groups') {
      setGroupKey(key ?? '');
      setSelectedId(null);
      setMobilePane('detail');
    } else {
      setSelectedId(null);
      setMobilePane('list');
    }
    setQuery('');
    setFilter('all');
  };

  const selectItem = async (a: AgentItem) => {
    if (a.id === selectedId && !form) {
      setMobilePane('detail');
      return;
    }
    if (!(await confirmDiscard())) return;
    setForm(null);
    setConflict(null);
    setGroupForm(null);
    if (groupKey === NEW_GROUP_KEY) setGroupKey('');
    setSelectedId(a.id);
    setMobilePane('detail');
  };

  const handleClose = async () => {
    if (saving || groupSaving || groupDeleting || busyId) return;
    if (!(await confirmDiscard())) return;
    resetEditing();
    onClose();
  };

  // --- 定義: 編集・保存・削除 -----------------------------------------------------
  const toForm = (a: AgentItem): AgentEditForm => ({
    id: a.id,
    type: a.type,
    scope: a.scope,
    name: a.name,
    description: a.description ?? '',
    body: a.body ?? '',
    enabled: a.enabled !== false,
    status: a.status ?? 'open',
    members: (a.shared_with ?? [])
      .filter((e) => e !== a.user_id && e !== currentUserEmail)
      .map((e) => ({ email: e, name: resolve(e) })),
    groupId: a.group_id ?? '',
    expectedUpdatedAt: a.updated_at ?? 0,
  });

  const startEdit = () => {
    if (!selectedItem) return;
    setConflict(null);
    setGroupForm(null);
    setForm(toForm(selectedItem));
  };

  const startCreate = async () => {
    if (!(await confirmDiscard())) return;
    const type: AgentType = section === 'groups' || section === 'automation' ? 'rule' : section;
    const gid = section === 'groups' && groupKey !== NEW_GROUP_KEY ? groupKey : '';
    setGroupForm(null);
    setConflict(null);
    setSelectedId(null);
    setForm({
      id: '',
      type,
      scope: 'personal',
      name: '',
      description: '',
      body: '',
      enabled: true,
      status: 'open',
      members: [],
      groupId: gid,
      expectedUpdatedAt: 0,
    });
    setMobilePane('detail');
  };

  const cancelEdit = () => {
    setForm(null);
    setConflict(null);
    if (isNewAgent) setMobilePane('list');
  };

  // 保存前に「見える人が変わる」場合は確認する (共有の副作用を明示する)
  const confirmAudienceChange = async (item: AgentItem, nextGroupId: string, nextMembers?: string[]) => {
    const before = audienceOf(item, item.group_id ?? '', groupById);
    let after: string[];
    if (nextGroupId) {
      after = audienceOf(item, nextGroupId, groupById);
    } else if (nextMembers) {
      after = [...new Set([item.user_id ?? currentUserEmail, currentUserEmail, ...nextMembers])];
    } else {
      after = audienceOf(item, '', groupById);
    }
    const others = (list: string[]) => list.filter((e) => e !== currentUserEmail);
    if (sameSet(others(before), others(after))) return true;
    const added = others(after).filter((e) => !before.includes(e));
    const removed = others(before).filter((e) => !after.includes(e));
    const lines: string[] = [];
    if (added.length > 0) lines.push(`新たに見える人: ${formatMembers(added)}`);
    if (removed.length > 0) lines.push(`見えなくなる人: ${formatMembers(removed)}`);
    return confirm({
      title: '共有範囲が変わります',
      message: `「${item.name}」の見える人が変わります。${lines.join(' / ')}。変更した相手には Chat で通知されます。続けますか？`,
      confirmText: '続ける',
    });
  };

  const agentFormValid = form !== null && form.name.trim() !== '' && form.body.trim() !== '' && !conflict;

  const handleSaveAgent = async () => {
    if (!form || !agentFormValid) return;
    const name = form.name.trim();
    const members = form.scope === 'shared' && !form.groupId ? form.members.map((m) => m.email) : undefined;
    const item = form.id ? itemById.get(form.id) : undefined;
    if (item) {
      const ok = await confirmAudienceChange(
        item,
        form.groupId,
        form.scope === 'shared' && !form.groupId ? members : form.scope === 'personal' && !form.groupId ? [] : undefined
      );
      if (!ok) return;
    }
    setSaving(true);
    try {
      await onSaveAgent({
        id: form.id || undefined,
        type: form.type,
        scope: form.scope,
        name,
        description: form.description.trim(),
        body: form.body,
        enabled: form.enabled,
        status: form.type === 'issue' ? form.status : undefined,
        sharedWith: members,
        groupId: form.groupId,
        expectedUpdatedAt: form.id ? form.expectedUpdatedAt : undefined,
      });
      notify.success(form.id ? '定義を保存しました' : '定義を登録しました');
      // 新規は種別の面へ移り、一覧の再取得後に表示される。グループ面で作った場合はその面に留まる
      if (!form.id && section !== 'groups') setSection(form.type);
      setForm(null);
      setConflict(null);
      if (!form.id) setMobilePane('list');
    } catch (e) {
      if (e instanceof AgentConflictError) {
        setConflict(e.latest);
        notify.warning('他の利用者または AI が先に更新しました。最新の内容を確認してください');
      } else {
        notify.error(e instanceof Error ? e.message : '定義の保存に失敗しました');
      }
    } finally {
      setSaving(false);
    }
  };

  // 競合時: 最新の内容 (名前・説明・本文) をフォームへ取り込み、最新の updated_at で保存できるようにする
  const adoptLatest = () => {
    if (!conflict || !form) return;
    setForm({
      ...form,
      name: conflict.name,
      description: conflict.description ?? '',
      body: conflict.body ?? '',
      expectedUpdatedAt: conflict.updated_at ?? 0,
    });
    setConflict(null);
  };
  // 競合時: 自分の入力を保ったまま、最新の updated_at で保存し直せるようにする
  const keepMine = () => {
    if (!conflict || !form) return;
    setForm({ ...form, expectedUpdatedAt: conflict.updated_at ?? 0 });
    setConflict(null);
  };

  // 本文に触れずに 1 項目だけ変える (行の有効スイッチ・課題のステータス)。共有先は未指定で現在値維持
  const quickSave = async (item: AgentItem, patch: Partial<Pick<SaveAgentInput, 'enabled' | 'status'>>, doneMessage: string) => {
    setBusyId(item.id);
    try {
      await onSaveAgent({
        id: item.id,
        type: item.type,
        scope: item.scope,
        name: item.name,
        description: item.description ?? '',
        body: item.body ?? '',
        enabled: item.enabled !== false,
        status: item.type === 'issue' ? item.status ?? 'open' : undefined,
        groupId: item.group_id ?? '',
        expectedUpdatedAt: item.updated_at,
        ...patch,
      });
      notify.success(doneMessage);
    } catch (e) {
      if (e instanceof AgentConflictError) {
        notify.warning('他の利用者または AI が先に更新したため、一覧を最新化しました。もう一度お試しください');
        await onRefresh?.();
      } else {
        notify.error(e instanceof Error ? e.message : '定義の更新に失敗しました');
      }
    } finally {
      setBusyId(null);
    }
  };

  const toggleEnabled = (item: AgentItem) => {
    if (form && form.id === item.id) {
      setForm({ ...form, enabled: !form.enabled });
      return;
    }
    const next = item.enabled === false;
    void quickSave(item, { enabled: next }, next ? '有効にしました (AI へ渡します)' : '無効にしました (AI へ渡しません)');
  };

  const setStatus = (item: AgentItem, status: IssueStatus) => {
    if ((item.status ?? 'open') === status) return;
    void quickSave(item, { status }, status === 'done' ? '完了にしました (AI へ渡しません)' : 'ステータスを更新しました');
  };

  const handleAssign = async (item: AgentItem, nextGroupId: string) => {
    if ((item.group_id ?? '') === nextGroupId) return;
    const ok = await confirmAudienceChange(item, nextGroupId);
    if (!ok) return;
    setBusyId(item.id);
    try {
      await onAssignGroup(item.id, nextGroupId, item.updated_at);
      notify.success(nextGroupId ? `「${groupById.get(nextGroupId)?.name ?? ''}」へ移動しました` : '共通へ移動しました');
    } catch (e) {
      if (e instanceof AgentConflictError) {
        notify.warning('他の利用者または AI が先に更新したため、一覧を最新化しました。もう一度お試しください');
        await onRefresh?.();
      } else {
        notify.error(e instanceof Error ? e.message : 'グループの変更に失敗しました');
      }
    } finally {
      setBusyId(null);
    }
  };

  const handleDeleteAgent = async () => {
    const item = selectedItem;
    if (!item) return;
    const audience = audienceOthers(item);
    const ok = await confirm({
      title: '定義の削除',
      message:
        `「${item.name}」を削除しますか？元に戻せません。` +
        (audience.length > 0 ? ` 共有相手 (${formatMembers(audience)}) からも消え、Chat で通知されます。` : ''),
      confirmText: '削除する',
      confirmIcon: <Icons.Delete />,
      destructive: true,
    });
    if (!ok) return;
    setBusyId(item.id);
    try {
      await onDeleteAgent(item.id);
      notify.success('定義を削除しました');
      setSelectedId(null);
      setForm(null);
      setConflict(null);
      setMobilePane('list');
    } catch (e) {
      notify.error(e instanceof Error ? e.message : '定義の削除に失敗しました');
    } finally {
      setBusyId(null);
    }
  };

  // --- グループ: 作成・編集・保存・削除 ---------------------------------------------
  const currentGroup = section === 'groups' && groupKey && groupKey !== NEW_GROUP_KEY ? groupById.get(groupKey) ?? null : null;

  const startGroupEdit = () => {
    if (!currentGroup) return;
    setForm(null);
    setConflict(null);
    setGroupForm({
      id: currentGroup.id,
      scope: currentGroup.scope,
      name: currentGroup.name,
      description: currentGroup.description ?? '',
      members: currentGroup.shared_with
        .filter((e) => e !== currentGroup.user_id && e !== currentUserEmail)
        .map((e) => ({ email: e, name: resolve(e) })),
      enabled: currentGroup.enabled,
    });
  };

  const startGroupCreate = async () => {
    if (!(await confirmDiscard())) return;
    setForm(null);
    setConflict(null);
    setSelectedId(null);
    setSection('groups');
    setGroupKey(NEW_GROUP_KEY);
    setGroupForm({ scope: 'personal', name: '', description: '', members: [], enabled: true });
    setMobilePane('detail');
  };

  const cancelGroupEdit = () => {
    setGroupForm(null);
    if (groupKey === NEW_GROUP_KEY) {
      setGroupKey('');
      setMobilePane('list');
    }
  };

  const groupFormValid = groupForm !== null && groupForm.name.trim() !== '';

  const handleSaveGroup = async () => {
    if (!groupForm || !groupFormValid) return;
    const name = groupForm.name.trim();
    if (groupForm.scope === 'shared' && groupForm.members.length === 0 && !groupForm.id) {
      notify.warning('共有グループには共有メンバーを 1 人以上指定してください');
      return;
    }
    // 既存グループで見える人が変わる場合は、中の定義も同じ相手に見える旨を確認する
    if (groupForm.id) {
      const g = groupById.get(groupForm.id);
      if (g) {
        const before = (g.scope === 'shared' ? g.shared_with : [g.user_id]).filter((e) => e !== currentUserEmail);
        const after = (groupForm.scope === 'shared'
          ? [...new Set([g.user_id, currentUserEmail, ...groupForm.members.map((m) => m.email)])]
          : [g.user_id]
        ).filter((e) => e !== currentUserEmail);
        if (!sameSet(before, after)) {
          const added = after.filter((e) => !before.includes(e));
          const removed = before.filter((e) => !after.includes(e));
          const lines: string[] = [];
          if (added.length > 0) lines.push(`新たに見える人: ${formatMembers(added)}`);
          if (removed.length > 0) lines.push(`見えなくなる人: ${formatMembers(removed)}`);
          const count = countByGroup.get(g.id) ?? 0;
          const ok = await confirm({
            title: '共有範囲が変わります',
            message: `グループ「${g.name}」の見える人が変わります。${lines.join(' / ')}。中の定義 ${count} 件も同じ相手に見える / 見えなくなり、変更した相手には Chat で通知されます。続けますか？`,
            confirmText: '続ける',
          });
          if (!ok) return;
        }
      }
    }
    setGroupSaving(true);
    try {
      await onSaveGroup({
        id: groupForm.id,
        scope: groupForm.scope,
        name,
        description: groupForm.description.trim(),
        enabled: groupForm.enabled,
        // 共有時は更新後の全メンバーを渡す (作成者・本人はバックエンドが自動で補完する)
        sharedWith: groupForm.scope === 'shared' ? groupForm.members.map((m) => m.email) : undefined,
      });
      notify.success(groupForm.id ? 'グループを更新しました' : 'グループを作成しました');
      if (!groupForm.id) setPendingGroupName(name);
      setGroupForm(null);
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'グループの保存に失敗しました');
    } finally {
      setGroupSaving(false);
    }
  };

  const handleDeleteGroup = async () => {
    const g = currentGroup;
    if (!g) return;
    const count = countByGroup.get(g.id) ?? 0;
    if (count > 0) {
      notify.warning(`グループ「${g.name}」には定義が ${count} 件残っています。先に別グループか共通へ移してください`);
      return;
    }
    const ok = await confirm({
      title: 'グループの削除',
      message:
        `グループ「${g.name}」を削除しますか？` +
        (g.scope === 'shared' ? ' 共有メンバーには Chat で通知されます。' : '') +
        ' このグループを選んでいた会話は共通扱いになります。',
      confirmText: '削除する',
      confirmIcon: <Icons.Delete />,
      destructive: true,
    });
    if (!ok) return;
    setGroupDeleting(true);
    try {
      await onDeleteGroup(g.id);
      notify.success('グループを削除しました');
      setGroupKey('');
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'グループの削除に失敗しました');
    } finally {
      setGroupDeleting(false);
    }
  };

  // --- ドラッグ (一覧の行 → 左レールのグループ) ----------------------------------------
  const dragSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const handleDragStart = (e: DragStartEvent) => {
    setDraggingItem(itemById.get(String(e.active.id)) ?? null);
  };
  const handleDragEnd = (e: DragEndEvent) => {
    const item = itemById.get(String(e.active.id));
    setDraggingItem(null);
    const overId = e.over ? String(e.over.id) : '';
    if (!item || !overId.startsWith('group:')) return;
    void handleAssign(item, overId.slice('group:'.length));
  };

  // --- 一覧 ---------------------------------------------------------------------
  const sectionItems = useMemo(() => {
    if (section === 'groups') {
      const key = groupKey === NEW_GROUP_KEY ? null : groupKey;
      return key === null ? [] : allItems.filter((a) => (a.group_id ?? '') === key);
    }
    if (section === 'automation') return [];
    return allItems.filter((a) => a.type === section);
  }, [allItems, section, groupKey]);

  const visibleItems = useMemo(() => {
    let list = sectionItems;
    if (filter === 'mine') list = list.filter((a) => audienceOthers(a).length === 0);
    if (filter === 'shared') list = list.filter((a) => audienceOthers(a).length > 0);
    if (filter === 'off') list = list.filter((a) => a.enabled === false);
    const q = query.trim().toLowerCase();
    if (q) list = list.filter((a) => `${a.name}\n${a.description ?? ''}\n${a.body ?? ''}`.toLowerCase().includes(q));
    // 課題は未完了を上、完了を下に並べる (リマインドとしての可視性を優先)
    if (section === 'issue' || section === 'groups') {
      list = [...list].sort((a, b) => (a.status === 'done' ? 1 : 0) - (b.status === 'done' ? 1 : 0));
    }
    return list;
  }, [sectionItems, filter, query, section, audienceOthers]);

  const listTitle =
    section === 'groups'
      ? groupKey === NEW_GROUP_KEY
        ? '新しいグループ'
        : groupKey
          ? `グループ「${groupById.get(groupKey)?.name ?? ''}」の定義`
          : '共通の定義'
      : section === 'automation'
        ? ''
        : KIND_LABEL[section];
  const listHint =
    section === 'groups'
      ? groupKey === NEW_GROUP_KEY
        ? '右でグループ名を入力して保存してください'
        : groupKey
          ? 'このグループを選んだチャットで渡されます'
          : 'すべてのチャットで渡されます'
      : section === 'automation'
        ? ''
        : KIND_HINT[section];

  // --- 右ペインの内容と保存バー --------------------------------------------------------
  const detailKey =
    section === 'groups'
      ? `group:${groupKey}:${groupForm ? 'edit' : 'view'}`
      : `agent:${form ? form.id || 'new' : selectedId ?? 'none'}:${form ? 'edit' : 'view'}`;

  // 保存バーは編集モードの間ずっと出す (保存ボタンの場所を迷わせない)。変更が無い間は「保存する」を無効にする
  const saveBarShow = section === 'groups' ? groupForm !== null : form !== null;
  const saveBarDirty = section === 'groups' ? groupDirty || isNewGroup : agentDirty || isNewAgent;
  const saveBarMessage =
    section === 'groups'
      ? !groupFormValid
        ? 'グループ名を入力すると保存できます'
        : saveBarDirty
          ? '未保存の変更があります'
          : '変更すると保存できます'
      : conflict
        ? '競合を解決してから保存できます'
        : !agentFormValid
          ? '名前と本文を入力すると保存できます'
          : saveBarDirty
            ? '未保存の変更があります'
            : '変更すると保存できます';
  const saveBarCanSave = (section === 'groups' ? groupFormValid : agentFormValid) && saveBarDirty;
  const saveBarSaving = section === 'groups' ? groupSaving : saving;
  const saveBarCancel = section === 'groups' ? cancelGroupEdit : cancelEdit;
  const saveBarSave = section === 'groups' ? () => void handleSaveGroup() : () => void handleSaveAgent();

  // 編集中のキーボード操作 (Esc = 取消 / Ctrl+Enter = 保存)
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const editing = section === 'groups' ? groupForm !== null : form !== null;
    if (!editing) return;
    if (e.key === 'Escape') {
      e.stopPropagation();
      saveBarCancel();
    } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      if (saveBarCanSave && !saveBarSaving) saveBarSave();
    }
  };

  const detailContent =
    section === 'groups' ? (
      <AgentGroupDetail
        group={currentGroup}
        isCommon={groupKey === ''}
        form={groupForm}
        onFormChange={setGroupForm}
        saving={groupSaving}
        deleting={groupDeleting}
        count={groupKey === NEW_GROUP_KEY ? 0 : countByGroup.get(groupKey) ?? 0}
        commonChars={commonChars}
        ownChars={groupKey && groupKey !== NEW_GROUP_KEY ? injectedCharsByGroup.get(groupKey) ?? 0 : 0}
        basePromptChars={basePromptChars}
        staffOptions={staffOptions}
        currentUserEmail={currentUserEmail}
        formatMembers={formatMembers}
        resolve={resolve}
        narrow={narrow}
        onBack={() => setMobilePane('list')}
        onEdit={startGroupEdit}
        onDelete={() => void handleDeleteGroup()}
      />
    ) : (
      <AgentDetail
        item={form && form.id === '' ? null : selectedItem}
        form={form}
        onFormChange={setForm}
        conflict={conflict}
        saving={saving}
        busy={selectedId !== null && busyId === selectedId}
        editable={selectedItem ? canEdit(selectedItem) : false}
        groups={enabledGroups}
        groupById={groupById}
        staffOptions={staffOptions}
        currentUserEmail={currentUserEmail}
        formatMembers={formatMembers}
        resolve={resolve}
        narrow={narrow}
        onBack={() => setMobilePane('list')}
        onEdit={startEdit}
        onDelete={() => void handleDeleteAgent()}
        onToggleEnabled={() => selectedItem && toggleEnabled(selectedItem)}
        onSetStatus={(s) => selectedItem && setStatus(selectedItem, s)}
        onAdoptLatest={adoptLatest}
        onKeepMine={keepMine}
      />
    );

  const detailPane = (
    <Box sx={{ position: 'relative', minWidth: 0, minHeight: 0, height: '100%', bgcolor: '#fafafa', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2, pt: 1.75, pb: 4 }}>
        <motion.div
          key={detailKey}
          initial={{ opacity: 0, x: 18 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, ease: EASE_OUT }}
          style={{ minWidth: 0, height: '100%' }}
        >
          {detailContent}
        </motion.div>
      </Box>
      <SaveBar
        show={saveBarShow}
        message={saveBarMessage}
        dirty={saveBarDirty}
        canSave={saveBarCanSave}
        saving={saveBarSaving}
        onCancel={saveBarCancel}
        onSave={saveBarSave}
      />
    </Box>
  );

  const listPane =
    section === 'automation' ? (
      // boxSizing は CssBaseline 未使用のため明示 (高さ 100% + 余白で枠からはみ出さないように)
      <Box sx={{ minWidth: 0, minHeight: 0, height: '100%', boxSizing: 'border-box', overflowY: 'auto', bgcolor: '#fafafa', px: 2, pt: 1.5, pb: 4, gridColumn: narrow ? undefined : '2 / span 2' }}>
        <AssistantMailAutomationTab
          active={open && section === 'automation'}
          groups={enabledGroups}
          allGroups={groups}
          agents={agents}
          currentUserEmail={currentUserEmail}
          onOpenSession={(id) => {
            onOpenSession?.(id);
            onClose();
          }}
        />
      </Box>
    ) : (
      <AgentList
        title={listTitle}
        hint={listHint}
        items={visibleItems}
        totalCount={sectionItems.length}
        loading={loading}
        selectedId={form && form.id === '' ? null : selectedId}
        query={query}
        onQuery={setQuery}
        filter={filter}
        onFilter={setFilter}
        showType={section === 'groups'}
        showGroup={section !== 'groups'}
        groupNameOf={(a) => (a.group_id ? groupById.get(a.group_id)?.name ?? '不明なグループ' : '共通')}
        audienceCountOf={(a) => audienceOthers(a).length}
        canEdit={canEdit}
        busyId={busyId}
        newDraft={form && form.id === '' ? { name: form.name, type: form.type } : null}
        canCreate={groupKey !== NEW_GROUP_KEY}
        onSelect={(a) => void selectItem(a)}
        onToggleEnabled={toggleEnabled}
        onCreate={() => void startCreate()}
      />
    );

  const railPane = (
    <AgentRail
      section={section}
      groupKey={groupKey}
      newGroupKey={NEW_GROUP_KEY}
      newGroupName={isNewGroup ? groupForm?.name ?? '' : null}
      countByType={countByType}
      groups={groups}
      countByGroup={countByGroup}
      meterRows={meterRows}
      basePromptChars={basePromptChars}
      dragging={draggingItem !== null}
      onNav={(s, k) => {
        void navTo(s, k);
        setMobilePane(s === 'groups' ? 'detail' : 'list');
      }}
      onNewGroup={() => void startGroupCreate()}
    />
  );

  const title =
    section === 'automation'
      ? 'エージェント設定 ・ メール自動処理'
      : section === 'groups'
        ? 'エージェント設定 ・ グループ'
        : `エージェント設定 ・ ${KIND_LABEL[section]}`;

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        // 編集中の Esc は取消として扱い、ダイアログは閉じない (handleKeyDown 側で処理)
        if (reason === 'escapeKeyDown' && (form || groupForm)) return;
        void handleClose();
      }}
      maxWidth="lg"
      fullWidth
      fullScreen={narrow}
      disableScrollLock
      PaperProps={{
        // 高さは Dialog の土台 (fixed の表示領域) に対する割合で指定し、MUI 既定の max-height calc(100% - 64px)
        // に収める。100vh 基準だと横スクロールバーが出ている時にその分だけ下端が画面外へはみ出して切れる
        sx: narrow ? {} : { height: '100%' },
        onKeyDown: handleKeyDown,
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          py: 0.5,
          px: 2,
          fontSize: '0.875rem',
          fontWeight: 600,
          borderBottom: 1,
          borderColor: 'divider',
          minHeight: 40,
        }}
      >
        {narrow && (
          <Tooltip title="区分を選ぶ">
            <IconButton size="small" onClick={() => setMobilePane(mobilePane === 'rail' ? 'list' : 'rail')} sx={{ mr: 0.25 }}>
              <Icons.MenuOpen sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
        <Icons.Settings sx={{ color: blueGrey[400], fontSize: 18 }} />
        <Typography component="span" noWrap sx={{ fontSize: 'inherit', fontWeight: 'inherit', flex: 1, minWidth: 0 }}>
          {title}
        </Typography>
        <Tooltip title="閉じる">
          <span>
            <IconButton size="small" onClick={() => void handleClose()} disabled={saving || groupSaving || groupDeleting || Boolean(busyId)}>
              <Icons.Close sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
      </DialogTitle>

      <DndContext sensors={dragSensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={() => setDraggingItem(null)}>
        {narrow ? (
          // 狭い幅: 一覧を土台に、レールは左から・詳細は右から重ねてスライドする
          <Box sx={{ position: 'relative', flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <Box sx={{ position: 'absolute', inset: 0 }}>{listPane}</Box>
            <AnimatePresence>
              {mobilePane === 'rail' && (
                <motion.div
                  key="rail"
                  initial={{ x: '-100%' }}
                  animate={{ x: 0 }}
                  exit={{ x: '-100%' }}
                  transition={{ duration: 0.3, ease: EASE_OUT }}
                  style={{ position: 'absolute', inset: 0, zIndex: 3 }}
                >
                  {railPane}
                </motion.div>
              )}
            </AnimatePresence>
            <AnimatePresence>
              {mobilePane === 'detail' && section !== 'automation' && (
                <motion.div
                  key="detail"
                  initial={{ x: '100%' }}
                  animate={{ x: 0 }}
                  exit={{ x: '100%' }}
                  transition={{ duration: 0.3, ease: EASE_OUT }}
                  style={{ position: 'absolute', inset: 0, zIndex: 2 }}
                >
                  {detailPane}
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'grid',
              gridTemplateColumns: '196px minmax(260px, 1fr) minmax(320px, 1.25fr)',
              // 行の高さを枠に固定する (指定が無いと中身の高さまで行が伸び、各ペインがスクロールせず下端が切れる)
              gridTemplateRows: 'minmax(0, 1fr)',
              overflow: 'hidden',
            }}
          >
            {railPane}
            {listPane}
            {section !== 'automation' && detailPane}
          </Box>
        )}
        <DragOverlay dropAnimation={null}>
          {draggingItem ? (
            <Chip
              icon={<Icons.DragIndicator />}
              label={draggingItem.name || '(名称未設定)'}
              sx={{ bgcolor: '#fff', boxShadow: 6, fontWeight: 600, maxWidth: 260, cursor: 'grabbing' }}
            />
          ) : null}
        </DragOverlay>
      </DndContext>
    </Dialog>
  );
}
